import * as d3 from 'd3';
import { defineTexturedGlassPatterns } from './glassPatternsDefs';

export function dN_Rect(svg, d, x, y, rects, labelTextOrOptions = null, subFrame = false, fullCorners = false, halfCorners = false,
  displayLabel = true, labelOptions = {}, drawFrame = true) {

  // Support both old positional style and new object style
  let labelText, options;
  if (typeof labelTextOrOptions === 'object' && labelTextOrOptions !== null && !Array.isArray(labelTextOrOptions)) {
    // New style: options object passed
    options = labelTextOrOptions;
    labelText = options.labelText ?? null;
    subFrame = options.subFrame ?? false;
    fullCorners = options.fullCorners ?? false;
    halfCorners = options.halfCorners ?? false;
    displayLabel = options.displayLabel ?? true;
    labelOptions = options.labelOptions ?? {};
    drawFrame = options.drawFrame ?? true;
  } else {
    // Old style: positional parameters
    labelText = labelTextOrOptions;
  }

  let currentX = x;
  let prev = {};

  const {
    fontSize = 14,
    fontColor = 'black',
    fontFamily = 'Arial',
    fontOpacity = 0.4,
  } = labelOptions;

  rects.forEach((rect, index) => {
    const {
      w,
      h = prev.h ?? 100,
      color = prev.color ?? 'white',
      glassType = prev.glassType || null,            // ← NEW
      glassDesign = prev.glassDesign = null,           // ← NEW
      frameWidth = prev.frameWidth ?? 0,
      frameColor = prev.frameColor ?? 'white',
      lineFrameColor = prev.lineFrameColor ?? frameColor,
      lineFrameThickness = prev.lineFrameThickness ?? 1,
      outerStrokeColor = prev.outerStrokeColor ?? 'black',
      outerStrokeWidth = prev.outerStrokeWidth ?? 1,
      forceSubFrame = prev.forceSubFrame || false,
      fullCorners = prev.fullCorners || false,
      halfCorners = prev.halfCorners || false,
      displayLabel = prev.displayLabel || true
    } = rect;

    let baseFill = color;
    let tintOverlay = null;
    
    // TINTED GLASS
    if (glassType === 'Tinted') {
      baseFill = 'white'; // base is solid white, then tint overlays
      switch (glassDesign?.toLowerCase()) {
        case 'grey':
          tintOverlay = 'rgba(60, 60, 60, 0.2)';
          break;
        case 'bronze':
          tintOverlay = 'rgba(90, 50, 0, 0.2)';
          break;
        default:
          console.warn('Unknown tinted glass design:', glassDesign);
      }
    }
  
    // TEXTURED GLASS
    if (glassType === 'Textured' && glassDesign) {
      const patternId = `${glassDesign.toLowerCase().replace(/\s/g, '')}Pattern`;
      tintOverlay = `url(#${patternId})`;
      defineTexturedGlassPatterns(svg, glassDesign); // also safe to call here
    }
    

    // === OUTER FRAME ===
    if (drawFrame && frameWidth > 0) {
      svg.append('rect')
        .attr('x', currentX - frameWidth)
        .attr('y', y - frameWidth)
        .attr('width', w + 2 * frameWidth  + lineFrameThickness)
        .attr('height', h + 2 * frameWidth)
        .attr('fill', frameColor)
        .attr('stroke', lineFrameColor)
        .attr('stroke-width', lineFrameThickness);
    }

    // === SUBFRAME (only for first rect) ===
    if (drawFrame && ((subFrame === true && index === 0) || (forceSubFrame === true))) {
      const subInset = frameWidth/2; // distance from outer frame to subframe

      svg.append('rect')
        .attr('x', currentX - frameWidth + subInset)
        .attr('y', y - frameWidth + subInset)
        .attr('width',  w + 2 * (frameWidth - subInset) + 1)
        .attr('height', h + 2 * (frameWidth - subInset))
        .attr('fill', 'none')
        .attr('stroke', 'black')
        .attr('stroke-width', 0.5);
    }

    // === BASE FILL (color or pattern or white for tinted) ===
    svg.append('rect')
      .attr('x', currentX)
      .attr('y', y)
      .attr('width', w + lineFrameThickness)
      .attr('height', h)
      .attr('fill', baseFill)
      .attr('stroke', outerStrokeColor)
      .attr('stroke-width', outerStrokeWidth);

    // === TINT OVERLAY (semi-transparent layer on top of base) ===
    if (tintOverlay) {
      svg.append('rect')
        .attr('x', currentX)
        .attr('y', y)
        .attr('width', w + lineFrameThickness)
        .attr('height', h)
        .attr('fill', tintOverlay)
        .attr('stroke', 'none');
    }

    // === LABEL ===
    const textToUse = labelText == null ? index + 1 : labelText;

    if (displayLabel){
    svg.append('text')
        .attr('x', currentX + 20) // you can center it with: currentX + w / 2
        .attr('y', y + 20 + fontSize / 3)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', fontSize)
        .attr('fill', fontColor)
        .attr('fill-opacity', fontOpacity)
        .attr('font-family', fontFamily)
        .text(textToUse);
    }

    // === RECTANGLE CORNERS ===
    const cornerColor = 'black';
    const cornerStrokeWidth = 0.5;
    const subInset = frameWidth/2; // distance from outer frame to subframe

    if (drawFrame && halfCorners) { //half corner
      svg.append('line')
        .attr('x1', currentX - frameWidth)   // Starting X coordinate
        .attr('y1', y - frameWidth)   // Starting Y coordinate
        .attr('x2', currentX - frameWidth + subInset)     // Ending X coordinate
        .attr('y2', y - frameWidth + subInset)     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width
 
      svg.append('line')
        .attr('x1', currentX - frameWidth + w + 2 * frameWidth  + lineFrameThickness)   // Starting X coordinate
        .attr('y1', y - frameWidth)   // Starting Y coordinate
        .attr('x2', currentX - frameWidth + subInset + w + 2 * (frameWidth - subInset) + 1 )     // Ending X coordinate
        .attr('y2', y - frameWidth + subInset )     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width  

      svg.append('line')
        .attr('x1', currentX - frameWidth)   // Starting X coordinate
        .attr('y1', y - frameWidth + h + 2 * frameWidth)   // Starting Y coordinate
        .attr('x2', currentX - frameWidth + subInset)     // Ending X coordinate
        .attr('y2', y - frameWidth + subInset + h + 2 * (frameWidth - subInset))     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width

      svg.append('line')
        .attr('x1', currentX - frameWidth + w + 2 * frameWidth  + lineFrameThickness)   // Starting X coordinate
        .attr('y1', y - frameWidth + h + 2 * frameWidth)   // Starting Y coordinate
        .attr('x2', currentX - frameWidth + subInset + w + 2 * (frameWidth - subInset) + 1)     // Ending X coordinate
        .attr('y2', y - frameWidth + subInset + h + 2 * (frameWidth - subInset))     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width
    }
    if (drawFrame && fullCorners) { //full corner
      svg.append('line')
        .attr('x1', currentX - frameWidth)   // Starting X coordinate
        .attr('y1', y - frameWidth)   // Starting Y coordinate
        .attr('x2', currentX)     // Ending X coordinate
        .attr('y2', y)     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width
 
      svg.append('line')
        .attr('x1', currentX - frameWidth + w + 2 * frameWidth  + lineFrameThickness)   // Starting X coordinate
        .attr('y1', y - frameWidth)   // Starting Y coordinate
        .attr('x2', currentX + w + lineFrameThickness)     // Ending X coordinate
        .attr('y2', y)     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width  

      svg.append('line')
        .attr('x1', currentX - frameWidth)   // Starting X coordinate
        .attr('y1', y - frameWidth + h + 2 * frameWidth)   // Starting Y coordinate
        .attr('x2', currentX)     // Ending X coordinate
        .attr('y2', y + h)     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width

      svg.append('line')
        .attr('x1', currentX - frameWidth + w + 2 * frameWidth  + lineFrameThickness)   // Starting X coordinate
        .attr('y1',  y - frameWidth + h + 2 * frameWidth)   // Starting Y coordinate
        .attr('x2', currentX + w + lineFrameThickness)     // Ending X coordinate
        .attr('y2', y + h)     // Ending Y coordinate
        .attr('stroke', cornerColor) // Line color
        .attr('stroke-width', cornerStrokeWidth); // Line width  
    } 
    currentX += w + 2 * frameWidth + d + 2*lineFrameThickness;

    prev = {
      w, h, color,
      frameWidth, frameColor,
      lineFrameColor, lineFrameThickness,
      outerStrokeColor, outerStrokeWidth,
      fullCorners, halfCorners, displayLabel,
      glassType, glassDesign
    };
  });
}


export function d_Rect(svg, x, y, w, h,
    color = 'white',
    frameWidth = 0,
    frameColor = 'white',
    lineFrameColor = null,
    lineFrameThickness = 1,
    outerStrokeColor = 'black',
    outerStrokeWidth = 1) {
return;
// 1. Outer stroke (e.g., black outline)
if (outerStrokeColor !== 'none' && outerStrokeWidth > 0) {
svg.append('rect')
.attr('x', x - outerStrokeWidth / 2)
.attr('y', y - outerStrokeWidth / 2)
.attr('width', w + outerStrokeWidth)
.attr('height', h + outerStrokeWidth)
.attr('fill', 'none')
.attr('stroke', outerStrokeColor)
.attr('stroke-width', outerStrokeWidth);
}

// 2. Frame (colored area)
if (frameWidth > 0) {
svg.append('rect')
.attr('x', x)
.attr('y', y)
.attr('width', w)
.attr('height', h)
.attr('fill', frameColor)
.attr('stroke', 'none');
}

// 3. Fill (inside frame)
const innerX = x + frameWidth;
const innerY = y + frameWidth;
const innerW = w - 2 * frameWidth;
const innerH = h - 2 * frameWidth;

svg.append('rect')
.attr('x', innerX)
.attr('y', innerY)
.attr('width', innerW)
.attr('height', innerH)
.attr('fill', color)
.attr('stroke', 'none');

// 4. Optional inner "line frame"
if (lineFrameColor !== 'none' && lineFrameThickness > 0) {
svg.append('rect')
.attr('x', innerX + lineFrameThickness / 2)
.attr('y', innerY + lineFrameThickness / 2)
.attr('width', innerW - lineFrameThickness)
.attr('height', innerH - lineFrameThickness)
.attr('fill', 'none')
.attr('stroke', lineFrameColor || frameColor)
.attr('stroke-width', lineFrameThickness);
}
}

// Function to erase/paint over rectangles with background color
export function dN_Rect_Erase(svg, d, x, y, rects, bgColor = 'white') {
  let currentX = x;
  let prev = {};

  rects.forEach((rect) => {
    const {
      w,
      h = prev.h ?? 100,
      frameWidth = prev.frameWidth ?? 0,
    } = rect;

    // Calculate the total width and height including frame
    const totalWidth = w + 2 * frameWidth + 2;
    const totalHeight = h + 2 * frameWidth;

    // Draw a solid rectangle covering the entire area (frame + content)
    svg.append('rect')
      .attr('x', currentX - frameWidth)
      .attr('y', y - frameWidth)
      .attr('width', totalWidth)
      .attr('height', totalHeight)
      .attr('fill', bgColor)
      .attr('stroke', 'none');

    currentX += w + 2 * frameWidth + d + 2;

    prev = { w, h, frameWidth };
  });
}

export function parseShapeOrientation(str) {
  const [shapeRaw, orientationRaw] = str?.trim().split(/\s+/) ?? [];

  const shape =
    !shapeRaw || shapeRaw === "None"
      ? "Rectangle"
      : shapeRaw;

  const orientation = orientationRaw?.toLowerCase() ?? null;

  return { shape, orientation };
}
